import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius as uiBorderRadius, fontSizes, lineHeights, rawColors as uiColors } from "@magicbi/ui-library";
import type { StatusBannerAppearance, StatusBannerTone } from "./StatusBanner.types";

const TONE = {
  default: { bg: rawColors.primary[50], fg: uiColors.text.strongEmphasis, chipBg: rawColors.structural.white, chipBorder: rawColors.stroke[200] },
  attention: { bg: rawColors.accent.amberLight, fg: uiColors.text.strongEmphasis, chipBg: rawColors.structural.white, chipBorder: rawColors.stroke[200] },
  inverse: { bg: rawColors.grey[900], fg: rawColors.text.white, chipBg: "transparent", chipBorder: rawColors.grey[600] },
  neutral: { bg: rawColors.structural[50], fg: uiColors.text.strongEmphasis, chipBg: rawColors.structural.white, chipBorder: rawColors.stroke[200] },
  error: { bg: rawColors.accent.pinkLight, fg: rawColors.accent.pink, chipBg: rawColors.structural.white, chipBorder: rawColors.stroke[200] },
} as const;

/** Full-bleed band: no radius, one 2.5rem line, side padding only, dark raised surface. */
const $flushCss = (flush?: boolean) =>
  flush
    ? css`
        min-height: 2.5rem;
        padding: 0 ${spacing[5]};
        border-radius: 0;
        background: ${rawColors.structural.ink};
      `
    : "";

/** Design `.banner`: flex, gap 10, 12/16 padding, radius 10, 13/20 medium. */
export const Container = styled.div<{ $tone: StatusBannerTone; $appearance: StatusBannerAppearance; $flush?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]} ${spacing[3]};
  width: 100%;
  box-sizing: border-box;
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  ${typographyMixin("caption", 2)}
  font-size: ${fontSizes["13"]};
  line-height: ${lineHeights["20"]};
  position: relative;
  background: ${({ $tone }) => TONE[$tone].bg};
  color: ${({ $tone }) => TONE[$tone].fg};

  /* The design's .banner: the tertiary surface (default tone), radius 10, medium weight. */
  ${({ $appearance, $tone, theme }) =>
    $appearance === "banner" &&
    css`
      border-radius: ${uiBorderRadius[10]};
      font-weight: 500;
      ${$tone === "default" ? `background: ${theme.colors.background.tertiary};` : ""}
    `}
  ${({ $flush }) =>
    $flushCss($flush)}
`;

export const Leading = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
`;

export const Body = styled.div`
  display: flex;
  flex: 1 1 12rem;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[1]} ${spacing[3]};
  min-width: 0;
`;

export const Title = styled.span<{ $strong?: boolean }>`
  font-weight: ${({ $strong }) => ($strong ? 700 : 600)};
`;

export const Detail = styled.span<{ $muted?: boolean }>`
  min-width: 0;
  ${({ $muted }) => ($muted ? `color: ${rawColors.stroke.strong};` : "")}
`;

export const IssueList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const IssueChip = styled.li<{ $tone: StatusBannerTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => TONE[$tone].chipBorder};
  background: ${({ $tone }) => TONE[$tone].chipBg};
`;

export const FixButton = styled.button`
  all: unset;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${spacing[2]};
  margin-left: auto;
`;

export const Meta = styled.span`
  margin-left: auto;
  opacity: 0.65;
`;

export const ActionSlot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
`;

export const ActionButton = styled.button<{ $flush?: boolean }>`
  height: ${({ $flush }) => ($flush ? "1.625rem" : "1.5rem")};
  padding: 0 ${spacing[2.5]};
  border: 1px solid currentColor;
  border-radius: ${borderRadius.full};
  background: transparent;
  color: inherit;
  ${typographyMixin("caption", 2)}
  cursor: pointer;
  ${({ $flush }) =>
    $flush
      ? css`
          border-color: transparent;
          background: ${uiColors.text.strongEmphasis};
        `
      : ""}

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Track = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 0.125rem;
  background: currentColor;
  opacity: 0.2;
`;

export const Fill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => $value}%;
  background: currentColor;
`;

export const inverseHint = css``;
